import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import {
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  VStack,
} from "../../../components/utils";
import { Input } from "../../../components/shared/InputField";
import { Select } from "../../../components/shared/Select";
import { getCurrencySymbol } from "../../../utils/currencyFormatter";
import {
  AllocationFactor,
  AllocationMode,
  CostOrGain,
  FieldDataType,
  NoMatchBehaviour,
  Rule,
  RuleScope,
  QuantumMode,
  useAllocationMatrixStore,
} from "../../../store/useAllocationMatrixStore";
import { useGetPlatformFields } from "../../../queries/allocationMatrix";

function ConditionDropdown({
  field,
  selectedValues,
  onChange,
  disabled = false,
}: {
  field: AllocationFactor;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        minWidth: Math.max(rect.width, 220),
        zIndex: 9999,
      });
    }
  }, [open]);

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      )
        return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  function numericLabel() {
    if (!selectedValues[0]) return `${field.name}: Any`;
    const [op, val] = selectedValues[0].split(":");
    if (!op || !val) return `${field.name}: Any`;
    const opSymbol = op === "lt" ? "<" : op === "gt" ? ">" : "=";
    return `${field.name}: ${opSymbol} ${val}`;
  }

  const label =
    field.dataType === FieldDataType.NUMERIC_RANGE
      ? numericLabel()
      : selectedValues.length === 0
      ? `${field.name}: Any`
      : selectedValues.length === 1
      ? `${field.name}: ${selectedValues[0]}`
      : `${field.name}: ${selectedValues.length} selected`;

  function toggleValue(value: string) {
    onChange(
      selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value]
    );
  }

  const isActive = selectedValues.length > 0;

  const numericParts = selectedValues[0]?.split(":") ?? [];
  const currentOp = numericParts[0] || "lt";
  const currentVal = numericParts[1] ?? "";

  function applyNumeric(newOp: string, newVal: string) {
    onChange(newVal === "" ? [] : [`${newOp}:${newVal}`]);
  }

  const dropdown = open ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-white border border-gray-200 rounded-md shadow-xl"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-2 border-b">
        <span className="text-sm font-semibold text-black-501">
          {field.name}
        </span>
      </div>
      <div className="p-1 max-h-56 overflow-y-auto">
        {(field.dataType === FieldDataType.ENUM ||
          field.dataType === FieldDataType.DROPDOWN ||
          field.dataType === FieldDataType.STRING ||
          field.dataType === FieldDataType.BOOLEAN ||
          field.dataType === FieldDataType.NUMBER ||
          field.dataType === FieldDataType.DATE) &&
          (field.dataType === FieldDataType.BOOLEAN
            ? ["true", "false"]
            : field.valueSet ?? []
          ).map((value) => (
            <label
              key={value}
              className={`flex items-center gap-2 px-3 py-1.5 rounded ${
                disabled
                  ? "cursor-not-allowed"
                  : "hover:bg-gray-50 cursor-pointer"
              }`}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(value)}
                disabled={disabled}
                onChange={() => toggleValue(value)}
                className={`accent-orange-501 ${
                  disabled ? "cursor-not-allowed" : "cursor-pointer"
                }`}
              />
              <span className="text-sm text-gray-700">
                {field.dataType === FieldDataType.BOOLEAN
                  ? value === "true"
                    ? "True"
                    : "False"
                  : value}
              </span>
            </label>
          ))}
        {field.dataType === FieldDataType.NUMERIC_RANGE && (
          <div className="px-3 py-3 flex flex-col gap-2">
            <div className="flex gap-1.5">
              {(
                [
                  { key: "lt", label: "< Less than" },
                  { key: "gt", label: "> Greater than" },
                  { key: "eq", label: "= Equal to" },
                ] as const
              ).map(({ key, label: opLabel }) => (
                <button
                  key={key}
                  type="button"
                  disabled={disabled}
                  onClick={() => applyNumeric(key, currentVal)}
                  className={`flex-1 px-2 py-1.5 text-xs rounded border transition-colors whitespace-nowrap ${
                    currentOp === key
                      ? "border-orange-400 text-orange-600 bg-white font-semibold"
                      : "border-gray-200 text-gray-500 bg-white hover:border-gray-400 hover:text-gray-700"
                  } ${
                    disabled
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  }`}
                >
                  {opLabel}
                </button>
              ))}
            </div>
            <input
              type="number"
              min={0}
              disabled={disabled}
              value={currentVal}
              placeholder="Enter value"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => applyNumeric(currentOp, e.target.value)}
              className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-orange-400 disabled:cursor-not-allowed disabled:bg-gray-50"
            />
          </div>
        )}
        {field.dataType === FieldDataType.DATE_RANGE && (
          <p className="text-xs text-gray-400 px-3 py-2">
            Date range conditions not yet supported.
          </p>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 px-2 py-1 border rounded text-sm bg-white transition-colors whitespace-nowrap ${
          isActive
            ? "border-orange-300 text-orange-600"
            : "border-gray-300 text-gray-600 hover:border-orange-300"
        }`}
      >
        {label}
        <Icon icon="ri:arrow-down-s-line" height={16} />
      </button>
      {ReactDOM.createPortal(dropdown, document.body)}
    </>
  );
}

// ─── QuantumInput ─────────────────────────────────────────────────────────────

function QuantumInput({
  placeholder,
  value,
  onChange,
  disabled = false,
}: {
  placeholder: string;
  value: number | null;
  onChange: (v: number | null) => void;
  disabled?: boolean;
}) {
  return (
    <Input
      type="number"
      min={0}
      placeholder={placeholder}
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) =>
        onChange(e.target.value === "" ? null : parseFloat(e.target.value))
      }
      className="flex-1 min-w-0 text-center"
    />
  );
}

const QUANTUM_MODE_OPTIONS = [
  { value: QuantumMode.MIN, label: "Min" },
  { value: QuantumMode.MAX, label: "Max" },
  { value: QuantumMode.RANGE, label: "Range" },
];

const SCOPE_OPTIONS = [
  { value: RuleScope.PER_EMPLOYEE, label: "Per Employee" },
  { value: RuleScope.PER_GRANT, label: "Per Grant" },
];

const COST_OR_GAIN_OPTIONS = [
  { value: CostOrGain.COST, label: "Cost" },
  { value: CostOrGain.GAIN, label: "Gain" },
];

function RuleRow({
  rule,
  index,
  onUpdate,
  onRemove,
  selectedFields,
  allPriorities,
  readOnly = false,
}: {
  rule: Rule;
  index: number;
  onUpdate: (i: number, r: Rule) => void;
  onRemove: (i: number) => void;
  selectedFields: AllocationFactor[];
  allPriorities: number[];
  readOnly?: boolean;
}) {
  const currencySymbol = getCurrencySymbol();
  const priorityInputRef = useRef<HTMLInputElement>(null);
  const { data: platformFields = [] } = useGetPlatformFields();

  useLayoutEffect(() => {
    if (priorityInputRef.current) {
      const length = String(rule.priority).length;
      const width = Math.max(56, length * 16 + 40);
      priorityInputRef.current.style.width = `${width}px`;
    }
  }, [rule.priority]);

  const allocationModeOptions = [
    { value: AllocationMode.FIXED_OPTIONS, label: "Fixed Options" },
    {
      value: AllocationMode.ABSOLUTE_VALUE,
      label: `Absolute Value (${currencySymbol})`,
    },
  ];

  const conditions: Record<string, string[]> =
    typeof rule.conditions === "object" && rule.conditions !== null
      ? rule.conditions
      : {};

  const isDuplicatePriority =
    allPriorities.filter((p) => p === rule.priority).length > 1;

  function update(partial: Partial<Rule>) {
    onUpdate(index, { ...rule, ...partial });
  }

  function updateCondition(fieldPath: string, values: string[]) {
    const newConditions = { ...conditions };
    if (values.length === 0) {
      delete newConditions[fieldPath];
    } else {
      newConditions[fieldPath] = values;
    }
    update({ conditions: newConditions });
  }

  const enrichedFields = selectedFields.map((field) => {
    const platformField = platformFields.find(
      (pf) => pf.fieldPath === field.fieldPath
    );
    return platformField
      ? { ...field, valueSet: platformField.valueSet }
      : field;
  });

  return (
    <HStack className="px-4 py-4 items-start gap-6 border-b last:border-b-0">
      {/* Priority */}
      <VStack className="flex-shrink-0 gap-1">
        <input
          ref={priorityInputRef}
          type="number"
          min={1}
          disabled={readOnly}
          value={rule.priority}
          onChange={(e) =>
            update({ priority: parseInt(e.target.value, 10) || 1 })
          }
          className={`text-center transition-all border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-orange-400 disabled:bg-gray-50 disabled:cursor-not-allowed ${
            isDuplicatePriority ? "border-red-400 focus:border-red-400" : ""
          }`}
        />
        {isDuplicatePriority && (
          <span className="text-xs text-red-500 text-center">Duplicate</span>
        )}
      </VStack>

      {/* WHEN */}
      <HStack className="flex-1 flex-wrap items-center gap-2">
        {enrichedFields.length === 0 ? (
          <span className="text-xs text-gray-400">
            No factors defined — go back to step 1.
          </span>
        ) : (
          enrichedFields.map((field, i) => (
            <React.Fragment key={field.fieldPath}>
              {i > 0 && (
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  AND
                </span>
              )}
              <ConditionDropdown
                field={field}
                selectedValues={conditions[field.fieldPath] ?? []}
                onChange={(values) => updateCondition(field.fieldPath, values)}
                disabled={readOnly}
              />
            </React.Fragment>
          ))
        )}
      </HStack>

      {/* ALLOCATE */}
      <VStack className="w-96 flex-shrink-0 gap-2">
        <HStack className="gap-2 items-center">
          <Select
            options={QUANTUM_MODE_OPTIONS}
            value={rule.quantumMode}
            valueGetter={(o) => o.value}
            textGetter={(o) => o.label}
            isPlaceHolderDisabled
            disabled={readOnly}
            onChange={(e) =>
              update({
                quantumMode: e.target.value as QuantumMode,
                quantumMin: null,
                quantumMax: null,
                quantumRecommended: null,
              })
            }
            className="flex-1"
          />
          <Select
            options={SCOPE_OPTIONS}
            value={rule.scope}
            valueGetter={(o) => o.value}
            textGetter={(o) => o.label}
            isPlaceHolderDisabled
            disabled={readOnly}
            onChange={(e) => update({ scope: e.target.value as RuleScope })}
            className="flex-1"
          />
          <button
            type="button"
            disabled={readOnly}
            onClick={() => onRemove(index)}
            className={`flex-shrink-0 ${
              readOnly
                ? "text-gray-200 cursor-not-allowed"
                : "text-gray-400 hover:text-red-500"
            }`}
          >
            <Icon icon="ri:delete-bin-line" height={18} />
          </button>
        </HStack>

        <Select
          options={allocationModeOptions}
          value={rule.allocationMode}
          valueGetter={(o) => o.value}
          textGetter={(o) => o.label}
          isPlaceHolderDisabled
          disabled={readOnly}
          onChange={(e) =>
            update({
              allocationMode: e.target.value as AllocationMode,
              costOrGain: null,
            })
          }
          className="w-full"
        />

        {rule.allocationMode === AllocationMode.ABSOLUTE_VALUE && (
          <Select
            options={COST_OR_GAIN_OPTIONS}
            value={rule.costOrGain ?? ""}
            valueGetter={(o) => o.value}
            textGetter={(o) => o.label}
            placeholder="Select cost or gain"
            disabled={readOnly}
            onChange={(e) =>
              update({
                costOrGain: (e.target.value as CostOrGain) || null,
              })
            }
            className="w-full"
          />
        )}

        <HStack className="gap-2">
          {rule.quantumMode === QuantumMode.MIN && (
            <QuantumInput
              placeholder={
                rule.allocationMode === AllocationMode.ABSOLUTE_VALUE
                  ? `Enter min value (${currencySymbol})`
                  : "Enter min options"
              }
              value={rule.quantumMin}
              disabled={readOnly}
              onChange={(v) => update({ quantumMin: v, quantumRecommended: v })}
            />
          )}
          {rule.quantumMode === QuantumMode.MAX && (
            <QuantumInput
              placeholder={
                rule.allocationMode === AllocationMode.ABSOLUTE_VALUE
                  ? `Enter max value (${currencySymbol})`
                  : "Enter max options"
              }
              value={rule.quantumMax}
              disabled={readOnly}
              onChange={(v) => update({ quantumMax: v, quantumRecommended: v })}
            />
          )}
          {rule.quantumMode === QuantumMode.RANGE && (
            <>
              <QuantumInput
                placeholder={
                  rule.allocationMode === AllocationMode.ABSOLUTE_VALUE
                    ? `Min (${currencySymbol})`
                    : "Min"
                }
                value={rule.quantumMin}
                disabled={readOnly}
                onChange={(v) => update({ quantumMin: v })}
              />
              <QuantumInput
                placeholder="Recommended"
                value={rule.quantumRecommended}
                disabled={readOnly}
                onChange={(v) => update({ quantumRecommended: v })}
              />
              <QuantumInput
                placeholder={
                  rule.allocationMode === AllocationMode.ABSOLUTE_VALUE
                    ? `Max (${currencySymbol})`
                    : "Max"
                }
                value={rule.quantumMax}
                disabled={readOnly}
                onChange={(v) => update({ quantumMax: v })}
              />
            </>
          )}
        </HStack>
      </VStack>
    </HStack>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const NO_MATCH_OPTIONS = [
  { value: NoMatchBehaviour.BLOCK, label: "Block" },
  { value: NoMatchBehaviour.FALLBACK_RULE, label: "Fallback rule (catch-all)" },
  { value: NoMatchBehaviour.MANUAL_OVERRIDE, label: "Manual override" },
];

interface AllocationMatrixRuleBuilderProps {
  onStepChange: (e?: number) => void;
  onBackClick: () => void;
  readOnly?: boolean;
}

const AllocationMatrixRuleBuilder = (
  props: AllocationMatrixRuleBuilderProps
) => {
  const { onStepChange, onBackClick, readOnly = false } = props;
  const store = useAllocationMatrixStore();
  const [error, setError] = useState(false);

  function handleAddRule() {
    const newRule: Rule = {
      priority: store.rules.length + 1,
      scope: RuleScope.PER_EMPLOYEE,
      allocationMode: AllocationMode.FIXED_OPTIONS,
      quantumMode: QuantumMode.RANGE,
      quantumMin: null,
      quantumMax: null,
      quantumRecommended: null,
      costOrGain: null,
      conditions: {},
    };
    store.addRule(newRule);
    setError(false);
  }

  const priorities = store.rules.map((r) => r.priority);
  const hasDuplicatePriorities = new Set(priorities).size !== priorities.length;

  function handleNext() {
    if (store.rules.length === 0) {
      setError(true);
      return;
    }
    if (hasDuplicatePriorities) {
      setError(true);
      return;
    }
    onStepChange();
  }

  return (
    <VStack className="w-full h-full">
      {/* Header */}
      <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between font-semibold text-lg1 text-black-501">
          Build Rules
        </HStack>
        <HStack className="flex font-medium text-sm3 text-gray-401">
          Each rule combines factor values (AND across factors, OR within).
          Lower priority wins on ties.
        </HStack>
      </VStack>

      {/* Body */}
      <VStack className="flex-1 overflow-y-auto px-6 pt-6 pb-2 gap-6">
        {/* Allocation rules section */}
        <VStack className="gap-3">
          <HStack className="items-start justify-between">
            <VStack className="gap-1">
              <p className="text-sm font-semibold text-black-501">
                Allocation rules
              </p>
              <p className="text-xs text-gray-401">
                Each rule maps a combination of factor values to an option
                quantum. Lower priority wins.
              </p>
            </VStack>
            <ButtonPrimary
              disabled={readOnly}
              onClick={handleAddRule}
              className="flex items-center gap-1 whitespace-nowrap"
            >
              <Icon icon="ri:add-line" height={16} className="inline" />
              Add rule
            </ButtonPrimary>
          </HStack>

          <VStack className="border rounded-md overflow-hidden">
            {store.rules.length === 0 ? (
              <p className="p-8 text-sm text-gray-400 text-center border-dashed border rounded-md">
                No rules yet. Add one to map factor combinations to an
                allocation.
              </p>
            ) : (
              <>
                <HStack className="px-4 py-2 bg-gray-50 border-b gap-6">
                  <span className="w-14 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Priority
                  </span>
                  <span className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    When
                  </span>
                  <span className="w-96 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Allocate
                  </span>
                </HStack>
                {store.rules.map((rule, index) => (
                  <RuleRow
                    key={index}
                    rule={rule}
                    index={index}
                    onUpdate={store.updateRule}
                    onRemove={store.removeRule}
                    selectedFields={store.selectedFactors}
                    allPriorities={priorities}
                    readOnly={readOnly}
                  />
                ))}
              </>
            )}
          </VStack>

          {error && (
            <Error
              text={
                hasDuplicatePriorities
                  ? "Each rule must have a unique priority number."
                  : "Add at least one rule to continue."
              }
            />
          )}
        </VStack>

        {/* No-match behaviour */}
        <HStack className="items-center justify-between px-4 py-3 border rounded-md">
          <VStack className="gap-0.5">
            <p className="text-sm font-semibold text-black-501">
              No-match behaviour
            </p>
            <p className="text-xs text-gray-401">
              When an employee doesn&apos;t match any rule.
            </p>
          </VStack>
          <Select
            options={NO_MATCH_OPTIONS}
            value={store.noMatchBehaviour}
            valueGetter={(o) => o.value}
            textGetter={(o) => o.label}
            isPlaceHolderDisabled
            disabled={readOnly}
            onChange={(e) =>
              store.setNoMatchBehaviour(e.target.value as NoMatchBehaviour)
            }
            className="w-56"
          />
        </HStack>
      </VStack>

      {/* Footer */}
      <HStack className="justify-between px-6 pt-4 pb-4 border-t-[0.5px]">
        <ButtonPrimary1 onClick={onBackClick}>
          <Icon
            icon="ri:arrow-left-s-line"
            height={16}
            className="inline mr-1"
          />
          Back
        </ButtonPrimary1>
        <ButtonPrimary onClick={handleNext}>
          Next
          <Icon
            icon="ri:arrow-right-s-line"
            height={16}
            className="inline ml-1"
          />
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
};

export default AllocationMatrixRuleBuilder;
